import { useMemo, useState } from 'react';
import { ResultCard } from '@/components/ResultCard';
import { FitDetailPanel } from '@/components/FitDetailPanel';
import { StatBarChart } from '@/components/analytics/StatBarChart';
import { useFitHistory } from '@/hooks/useFitHistory';
import { api } from '@/services/api';
import { garmentTypesForGroup } from '@/data/garmentTypes';
import type { ComparisonResult } from '@/types/domain';
import type { FitHistoryEntry } from '@/types/history';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function garmentTypeName(entry: FitHistoryEntry): string | null {
  if (!entry.garmentType) return null;
  return garmentTypesForGroup(entry.category).find((t) => t.slug === entry.garmentType)?.name ?? entry.garmentType;
}

function HistoryEntryCard({ entry, onRemove }: { entry: FitHistoryEntry; onRemove: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [liveResults, setLiveResults] = useState<ComparisonResult[] | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(entry.results[0]?.targetBrandSlug ?? null);
  const [refreshing, setRefreshing] = useState(false);

  const results = liveResults ?? entry.results;
  const top = results[0];
  const selected = results.find((r) => r.targetBrandSlug === selectedSlug) ?? results[0] ?? null;

  async function compareAgain() {
    setRefreshing(true);
    const res = entry.usedPassport
      ? null // a Passport-based comparison can only be safely re-run from the current Passport, not a frozen snapshot — see note below.
      : await api.compareEquivalent({
          audience: entry.audience,
          category: entry.category,
          garmentType: entry.garmentType,
          sourceBrandSlug: entry.sourceBrandSlug ?? undefined,
          sourceSize: entry.sourceSize ?? undefined,
        });
    setRefreshing(false);
    if (res?.success) {
      const data = res.data as { results: ComparisonResult[] };
      setLiveResults(data.results);
      setSelectedSlug(data.results[0]?.targetBrandSlug ?? null);
      setExpanded(true);
    }
  }

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-muted">{formatDate(entry.createdAt)}</p>
          <p className="mt-1 text-sm font-semibold text-ink">
            {entry.sourceDescription} — {entry.category === 'tops' ? 'Top half' : 'Bottom half'}
            {garmentTypeName(entry) ? ` · ${garmentTypeName(entry)}` : ''}
          </p>
          {top && (
            <p className="mt-1 text-sm text-muted">
              Best match: {top.targetBrandName} {top.recommendedSize} ({top.confidence} confidence)
              {results.length > 1 ? ` · +${results.length - 1} more` : ''}
            </p>
          )}
        </div>
        <button
          type="button"
          aria-label="Remove this history entry"
          onClick={onRemove}
          className="shrink-0 text-sm text-muted hover:text-danger"
        >
          ✕
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="min-h-[36px] rounded-xl border border-stone-200 px-3 text-xs font-semibold text-ink hover:border-brand/40"
        >
          {expanded ? 'Hide fit map' : 'View fit map again'}
        </button>
        {!entry.usedPassport && (
          <button
            type="button"
            onClick={compareAgain}
            disabled={refreshing}
            className="min-h-[36px] rounded-xl border border-stone-200 px-3 text-xs font-semibold text-ink hover:border-brand/40 disabled:opacity-50"
          >
            {refreshing ? 'Comparing…' : 'Compare again (live data)'}
          </button>
        )}
      </div>
      {entry.usedPassport && (
        <p className="mt-2 text-xs text-muted">
          This comparison used your Passport measurements at the time — update your Passport and run a new comparison to refresh it.
        </p>
      )}

      {expanded && (
        <div className="mt-4 grid gap-4 border-t border-stone-100 pt-4 lg:grid-cols-[1fr_240px]">
          <div className="grid gap-2 sm:grid-cols-2">
            {results.map((r) => (
              <ResultCard
                key={r.targetBrandSlug}
                result={r}
                selected={r.targetBrandSlug === selectedSlug}
                onSelect={() => setSelectedSlug(r.targetBrandSlug)}
              />
            ))}
          </div>
          <FitDetailPanel audience={entry.audience} category={entry.category} result={selected} />
        </div>
      )}
    </div>
  );
}

const CONFIDENCE_LABEL: Record<ComparisonResult['confidence'], string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

const CONFIDENCE_COLOR: Record<ComparisonResult['confidence'], string> = {
  high: 'bg-brand',
  medium: 'bg-stone-400',
  low: 'bg-danger',
};

export function FitHistoryPage() {
  const { history, remove } = useFitHistory();

  const insights = useMemo(() => {
    const confidenceCounts: Record<ComparisonResult['confidence'], number> = { high: 0, medium: 0, low: 0 };
    const brandCounts = new Map<string, number>();
    for (const entry of history) {
      const best = entry.results[0];
      if (best) confidenceCounts[best.confidence] += 1;
      brandCounts.set(entry.sourceDescription, (brandCounts.get(entry.sourceDescription) ?? 0) + 1);
    }
    const topBrands = [...brandCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
    return { confidenceCounts, topBrands };
  }, [history]);

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink">Fit History</h2>
        <p className="mt-1 text-sm text-muted">
          Your last {history.length > 0 ? history.length : ''} comparisons on this device, most recent first.
          "Compare again" re-runs against current chart data rather than replaying a stale snapshot.
        </p>
      </div>

      {history.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          <StatBarChart
            title="Confidence of your best matches"
            rows={(['high', 'medium', 'low'] as const).map((c) => ({
              key: c,
              label: CONFIDENCE_LABEL[c],
              value: insights.confidenceCounts[c],
              colorClass: CONFIDENCE_COLOR[c],
            }))}
          />
          <StatBarChart
            title="Most-compared brands"
            rows={insights.topBrands.map(([label, value], i) => ({
              key: label,
              label,
              value,
              colorClass: i === 0 ? 'bg-brand' : 'bg-stone-400',
            }))}
          />
        </div>
      )}

      {history.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-stone-200 p-4 text-center text-sm text-muted">
          No comparisons yet — run one from Equivalent Sizes and it'll show up here.
        </p>
      ) : (
        <div className="space-y-3">
          {history.map((entry) => (
            <HistoryEntryCard key={entry.id} entry={entry} onRemove={() => remove(entry.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
